import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { ApiService } from "../../services/api";
import type { Announcement } from "../../types/announcement";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from "../../constants/listToolbarButtons";
import checkPermission from "../../utils/permission";
import { useModal } from "../../hooks/useModal";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import remarkBreaks from "remark-breaks";
import AnnouncementDetailModal from "../../components/modals/AnnouncementDetailModal";
import { usePanels } from "../../contexts/PanelContext.jsx";

export default function AnnouncementsPage() {
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useModal();
  const { mainContentRightInsetActive } = usePanels();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [openDepartmentsFor, setOpenDepartmentsFor] = useState<string | null>(null);
  const [detailAnnouncement, setDetailAnnouncement] = useState<Announcement | null>(null);

  const extractKeyValuePairs = useCallback((message?: string) => {
    if (!message) return [] as Array<{ key: string; value: string }>;

    const lines = message
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .slice(0, 6);

    const pairs: Array<{ key: string; value: string }> = [];
    for (const line of lines) {
      const colonIndex = line.indexOf(":");
      if (colonIndex <= 0) continue;
      const key = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();
      if (!key || !value) continue;
      if (key.length > 40) continue;
      pairs.push({ key, value });
      if (pairs.length >= 3) break;
    }
    return pairs;
  }, []);

  const canCreateAnnouncement = checkPermission("announcement_create");
  const canUpdateAnnouncement = checkPermission("announcement_update");
  const canDeleteAnnouncement = checkPermission("announcement_delete");

  const handleEdit = useCallback(
    (announcement: Announcement) => {
      navigate(`/announcements/edit/${announcement.id}`);
    },
    [navigate]
  );

  const handleDeleteClick = useCallback(
    async (announcement: Announcement) => {
      const confirmed = await showConfirm({
        title: "Duyuruyu Sil",
        message: `"${announcement.title}" duyurusunu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
        confirmText: "Sil",
        confirmButtonVariant: "danger",
      });

      if (confirmed) {
        try {
          await ApiService.deleteAnnouncement(announcement.id);
          await showAlert({
            title: "Başarılı",
            message: "Duyuru başarıyla silindi.",
            type: "success",
          });
          setRefreshTrigger((prev) => prev + 1);
        } catch (error: any) {
          console.error("Duyuru silme hatası:", error);
          await showAlert({
            title: "Hata",
            message: error.message || "Duyuru silinirken hata oluştu.",
            type: "error",
          });
        }
      }
    },
    [showConfirm, showAlert]
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toggleDepartmentExpand = useCallback((announcementId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenDepartmentsFor((prev) => (prev === announcementId ? null : announcementId));
  }, []);

  const renderDepartmentInfo = (announcement: Announcement) => {
    if (announcement.departments && announcement.departments.length > 0) {
      const MAX_VISIBLE = 3;
      const departments = announcement.departments;
      const visibleDepartments = departments.slice(0, MAX_VISIBLE);
      const remainingCount = departments.length - MAX_VISIBLE;
      const hasMore = remainingCount > 0;
      const isOpen = openDepartmentsFor === announcement.id;

      return (
        <div className="relative flex flex-wrap gap-1 items-center">
          {visibleDepartments.map((dept, index) => (
            <span key={dept.id} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: dept.color || "#9ca3af" }} />
              <span className="font-medium text-gray-700">{dept.name}</span>
              {index < visibleDepartments.length - 1 && <span className="text-gray-400">,</span>}
            </span>
          ))}
          {hasMore && (
            <button
              onClick={(e) => toggleDepartmentExpand(announcement.id, e)}
              className="inline-flex items-center gap-0.5 ml-1 px-2 py-0.5 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors"
              type="button"
            >
              <span>+{remainingCount} Daha</span>
              <ChevronDown className="w-3 h-3" />
            </button>
          )}

          {isOpen && (
            <>
              <button
                type="button"
                className="fixed inset-0 z-[9998] cursor-default"
                onClick={() => setOpenDepartmentsFor(null)}
                aria-label="Kapat"
              />
              <div className="absolute right-0 bottom-full mb-2 z-[9999] w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white shadow-lg p-3 max-h-64 overflow-auto">
                <div className="text-xs font-semibold text-gray-700 mb-2">Gönderilen</div>
                <div className="flex flex-wrap gap-2">
                  {departments.map((dept) => (
                    <span
                      key={dept.id}
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-700"
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dept.color || "#9ca3af" }} />
                      <span className="max-w-[14rem] truncate">{dept.name}</span>
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      );
    } else {
      return <span className="font-medium text-[11px] text-gray-700">Genel</span>;
    }
  };

  const renderAnnouncementItem = useCallback(
    (announcement: Announcement, viewMode: string = "list") => {
      if (viewMode === "grid") {
        const pairs = extractKeyValuePairs(announcement.message);
        const hasPairs = pairs.length > 0;
        const departments = announcement.departments || [];
        const MAX_VISIBLE = 3;
        const visibleDepartments = departments.slice(0, MAX_VISIBLE);
        const remainingCount = Math.max(0, departments.length - MAX_VISIBLE);
        const hasMoreDepartments = remainingCount > 0;
        const deptPopoverOpen = openDepartmentsFor === announcement.id;

        return (
          <div
            key={announcement.id}
            className="relative bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow flex flex-col min-w-0 h-[500px]">
            <div className="flex items-center gap-3">
              <h3 className="text-base font-semibold text-gray-900 break-words leading-snug">{announcement.title}</h3>
            </div>

            <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 h-40 flex-shrink-0 overflow-y-auto">
              {hasPairs ? (
                <div className="divide-y divide-gray-200 h-full">
                  {pairs.map((p, idx) => {
                    return (
                      <div key={`${p.key}-${idx}`} className="flex items-start gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <div className="text-xs text-gray-500">{p.key}:</div>
                          <div className="text-sm font-medium text-gray-900 break-words">{p.value}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-4 py-3 h-full overflow-y-auto">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    rehypePlugins={[rehypeHighlight]}
                    components={{
                      a: ({ children, href }) => (
                        <a
                          href={href}
                          className="text-blue-600 hover:underline font-medium"
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                          target="_blank"
                          rel="noopener noreferrer">
                          {children}
                        </a>
                      ),
                      h1: ({ children }) => <h4 className="m-0 mb-2 text-base font-semibold text-gray-900 break-words">{children}</h4>,
                      h2: ({ children }) => <h5 className="m-0 mb-2 text-sm font-semibold text-gray-900 break-words">{children}</h5>,
                      h3: ({ children }) => <h6 className="m-0 mb-2 text-sm font-medium text-gray-900 break-words">{children}</h6>,
                      hr: () => <hr className="my-2 border-gray-200" />,
                      p: ({ children }) => <p className="m-0 mb-2 last:mb-0 whitespace-pre-wrap break-words text-sm text-gray-700">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc ml-5 my-2 space-y-1 text-sm text-gray-700">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal ml-5 my-2 space-y-1 text-sm text-gray-700">{children}</ol>,
                      li: ({ children }) => <li className="whitespace-pre-wrap break-words">{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                      em: ({ children }) => <em className="italic">{children}</em>,
                      pre: ({ children }) => (
                        <pre className="max-h-32 overflow-auto rounded bg-white border border-gray-200 p-2 text-xs leading-relaxed">{children}</pre>
                      ),
                      code: ({ children, className }) => <code className={`break-words ${className || ""}`}>{children}</code>,
                    }}>
                    {announcement.message}
                  </ReactMarkdown>
                </div>
              )}
            </div>

            <div className="mt-4 h-48 flex flex-col">
              <div className="flex items-start gap-3">
                {announcement.creator ? (
                  <>
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {announcement.creator.media?.url ? (
                        <img
                          src={announcement.creator.media.url}
                          alt={`${announcement.creator.firstName} ${announcement.creator.lastName}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-white text-xs font-medium">
                          {(announcement.creator.firstName || "").charAt(0)}
                          {(announcement.creator.lastName || "").charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-gray-900 font-semibold break-words">
                        {`${announcement.creator.firstName || ""} ${announcement.creator.lastName || ""}`.trim() || "Bilinmiyor"}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{formatDate(announcement.createdAt)}</div>
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-gray-500">{formatDate(announcement.createdAt)}</div>
                )}
              </div>

              {canUpdateAnnouncement && (
                <div className="mt-3 relative">
                  <div className="text-xs text-gray-500 mb-1">Gönderilen:</div>
                  {departments.length > 0 ? (
                    <div className="relative">
                      <div className="flex flex-wrap gap-1.5 items-center h-16 overflow-hidden pr-24">
                        {visibleDepartments.map((dept) => (
                          <span
                            key={dept.id}
                            className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700"
                          >
                            <span
                              className="w-2 h-2 rounded-full inline-block flex-shrink-0"
                              style={{ backgroundColor: dept.color || "#9ca3af" }}
                            />
                            <span className="truncate max-w-[10rem]">{dept.name}</span>
                          </span>
                        ))}
                      </div>

                      {hasMoreDepartments && (
                        <button
                          type="button"
                          onClick={(e) => toggleDepartmentExpand(announcement.id, e)}
                          className="absolute right-0 bottom-0 inline-flex items-center gap-1 rounded-full bg-blue-50 hover:bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 transition-colors"
                        >
                          <span>+{remainingCount} Daha</span>
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700">
                      Genel
                    </span>
                  )}

                  {deptPopoverOpen && departments.length > 0 && (
                    <>
                      <button
                        type="button"
                        className="fixed inset-0 z-[9998] cursor-default"
                        onClick={() => setOpenDepartmentsFor(null)}
                        aria-label="Kapat"
                      />
                      <div className="absolute left-0 right-0 bottom-full mb-2 z-[9999] rounded-xl border border-gray-200 bg-white shadow-lg p-3 max-h-64 overflow-auto">
                        <div className="flex flex-wrap gap-2">
                          {departments.map((dept) => (
                            <span
                              key={dept.id}
                              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-700"
                            >
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dept.color || "#9ca3af" }} />
                              <span className="max-w-[14rem] truncate">{dept.name}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="mt-auto pt-4 flex-shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setDetailAnnouncement(announcement);
                }}
                className="w-full mb-3 px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 transition-colors font-semibold text-sm"
              >
                Detay
              </button>

            {(canUpdateAnnouncement || canDeleteAnnouncement) && (
              <div className="flex-shrink-0">
                <div className="grid grid-cols-2 gap-3">
                  {canUpdateAnnouncement && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(announcement);
                      }}
                      className="flex items-center justify-center rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors font-semibold text-sm py-2.5"
                    >
                      Düzenle
                    </button>
                  )}
                  {canDeleteAnnouncement && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(announcement);
                      }}
                      className="flex items-center justify-center rounded-xl bg-red-50 hover:bg-red-100 text-red-700 transition-colors font-semibold text-sm py-2.5"
                    >
                      Sil
                    </button>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>
        );
      }

      return (
        <div key={announcement.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow mb-2">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 break-words mb-2 pb-2 border-b border-gray-100">
                {announcement.title}
              </h3>
              <div className="text-xs sm:text-sm text-gray-600 mb-3 mt-2 prose prose-sm max-w-none overflow-hidden">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    a: ({ children, href }) => (
                      <a
                        href={href}
                        className="text-blue-600 hover:underline font-medium"
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                        target="_blank"
                        rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                    p: ({ children }) => <p className="mb-2 whitespace-pre-wrap">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc ml-6 mb-2">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal ml-6 mb-2">{children}</ol>,
                    li: ({ children }) => <li className="mb-1 whitespace-pre-wrap">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                  }}>
                  {announcement.message}
                </ReactMarkdown>
              </div>

              <div className="flex items-start gap-3 pt-3 border-t border-gray-100">
                {announcement.creator && (
                  <>
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {announcement.creator.media?.url ? (
                        <img
                          src={announcement.creator.media.url}
                          alt={`${announcement.creator.firstName} ${announcement.creator.lastName}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-white text-xs font-medium">
                          {(announcement.creator.firstName || "").charAt(0)}
                          {(announcement.creator.lastName || "").charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-700 font-medium">
                        {`${announcement.creator.firstName || ""} ${announcement.creator.lastName || ""}`.trim() || "Bilinmiyor"}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{formatDate(announcement.createdAt)}</div>
                      {canUpdateAnnouncement && (
                        <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                          <span className="text-gray-500">Gönderilen:</span>
                          {renderDepartmentInfo(announcement)}
                        </div>
                      )}
                    </div>
                  </>
                )}
                {!announcement.creator && (
                  <div className="text-xs text-gray-500">
                    {formatDate(announcement.createdAt)}
                    {canUpdateAnnouncement && (
                      <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                        <span className="text-gray-500">Gönderilen:</span>
                        {renderDepartmentInfo(announcement)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {(canUpdateAnnouncement || canDeleteAnnouncement) && (
              <div className="flex gap-1 flex-shrink-0">
                {canUpdateAnnouncement && (
                  <button
                    onClick={() => handleEdit(announcement)}
                    className="flex items-center justify-center w-7 h-7 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50 transition-colors"
                    title="Duyuruyu Düzenle">
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                )}
                {canDeleteAnnouncement && (
                  <button
                    onClick={() => handleDeleteClick(announcement)}
                    className="flex items-center justify-center w-7 h-7 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50 transition-colors"
                    title="Duyuruyu Sil">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
    [
      handleEdit,
      handleDeleteClick,
      formatDate,
      canUpdateAnnouncement,
      canDeleteAnnouncement,
      renderDepartmentInfo,
      extractKeyValuePairs,
      openDepartmentsFor,
      toggleDepartmentExpand,
    ]
  );

  const announcementsActions = useMemo(
    () => (
      <div className={LIST_TOOLBAR_ROW}>
        {canCreateAnnouncement && (
          <button
            onClick={() => navigate("/announcements/new")}
            className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600`}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni Duyuru</span>
          </button>
        )}
      </div>
    ),
    [canCreateAnnouncement, navigate]
  );

  const announcementsTab: TabItemProps = useMemo(
    () => ({
      type: "grid",
      title: "Tüm Duyurular",
      path: "announcements?sortBy=createdAt&sortOrder=DESC",
      actions: announcementsActions,
      isSearch: false,
      showToggle: true,
      refreshKey: `announcements-${refreshTrigger}`,
      item: renderAnnouncementItem,
      gridClassName: mainContentRightInsetActive
        ? "grid grid-cols-1 md:grid-cols-2 gap-6 pb-8 items-start"  // Sağ panel açık: maksimum 2 kolon
        : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pb-8 items-start",  // Normal: 3 kolon
    }),
    [announcementsActions, renderAnnouncementItem, refreshTrigger, mainContentRightInsetActive]
  );

  const tabs = useMemo(() => [announcementsTab], [announcementsTab]);

  return (
    <>
      <TabPage title="Duyurular" tabs={tabs} />
      <AnnouncementDetailModal
        isOpen={!!detailAnnouncement}
        announcement={detailAnnouncement}
        onClose={() => setDetailAnnouncement(null)}
      />
    </>
  );
}
